import * as React from "react";
import { Button } from "@react-email/button";
import {
  Img,
  Heading,
  Row,
  Section,
  Column,
  Container,
  Link,
  Hr,
} from "@react-email/components";
import { Text } from "@react-email/text";
import Footer from "../components/Footer";
import Header from "../components/Header";
import Layout from "../components/Layout";

export const name = "EventTicketReport";
export const subject = "Ticket Report: {{event_name}} - {{period}}";

const RoundedContainer = ({
  children,
  style = {},
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => {
  return (
    <Container
      style={{
        borderRadius: "4px",
        marginBottom: "16px",
        border: `1px solid #333`,
        padding: "16px",
        ...style,
      }}
    >
      {children}
    </Container>
  );
};

export function template({
  period_label,
  period,
  event_name,
  organization_logo,
  cdn_url,
  display_start_at,
  display_total_sales,
  display_period_sales,
  display_total_count,
  display_period_count,
  display_total_ticket_quantity,
  ticket_types,
}: {
  period_label: string;
  period: string;
  event_name: string;
  organization_logo: string;
  cdn_url: string;
  event_url: string;
  display_start_at: string;
  display_total_count: string;
  display_period_count: string;
  display_total_sales: string;
  display_period_sales: string;
  display_total_ticket_quantity: string;
  ticket_types: {
    name: string;
    display_period_count: string;
    display_period_sales: string;
    display_total_count: string;
    display_total_sales: string;
    display_quantity: string;
  }[];
}) {
  return (
    <Layout language="en" title={`${event_name} - Report - ${period}`}>
      <Header src={organization_logo} />
      <Container>
        <Heading as="h2" style={title}>
          {`${event_name} ticket report`}
        </Heading>
        <Text
          style={style.metricsLabel}
        >{`Event Date: ${display_start_at}`}</Text>
      </Container>
      <Text style={style.metricsLabel}>{`${period_label} - ${period}:`}</Text>
      <RoundedContainer>
        <Section width={"100%"}>
          <Row>
            <Column width={"40%"}>
              <Text style={style.metricsValue}>Name</Text>
            </Column>
            <Column width="30%" align="right">
              <Text style={style.metricsValue}>Tickets</Text>
            </Column>
            <Column width="30%" align="right">
              <Text style={style.metricsValue}>Sales</Text>
            </Column>
          </Row>
          {ticket_types.map((tt) => (
            <Row>
              <Column width={"45%"}>
                <Text style={style.metricsLabel}>{tt.name}</Text>
              </Column>
              <Column width="25%" align="right">
                <Text style={style.metricsLabel}>
                  {tt.display_period_count}
                </Text>
              </Column>
              <Column width="30%" align="right">
                <Text style={style.metricsLabel}>
                  {tt.display_period_sales}
                </Text>
              </Column>
            </Row>
          ))}
          <Hr />
          <Row>
            <Column width={"45%"}>
              <Text style={style.metricsLabel}>Total</Text>
            </Column>
            <Column width="25%" align="right">
              <Text style={style.metricsLabel}>{display_period_count}</Text>
            </Column>
            <Column width="30%" align="right">
              <Text style={style.metricsLabel}>{display_period_sales}</Text>
            </Column>
          </Row>
        </Section>
      </RoundedContainer>
      <Section />
      <Text style={style.metricsLabel}>Total ticket sales so far:</Text>
      <RoundedContainer>
        <Section width={"100%"}>
          <Row>
            <Column width={"40%"}>
              <Text style={style.metricsValue}>Name</Text>
            </Column>
            <Column width="30%" align="right">
              <Text style={style.metricsValue}>Tickets</Text>
            </Column>
            <Column width="30%" align="right">
              <Text style={style.metricsValue}>Sales</Text>
            </Column>
          </Row>
          {ticket_types.map((tt) => (
            <Row>
              <Column width={"45%"}>
                <Text style={style.metricsLabel}>{tt.name}</Text>
              </Column>
              <Column width="25%" align="right">
                <Text style={style.metricsLabel}>
                  {`${tt.display_total_count} / ${tt.display_quantity}`}
                </Text>
              </Column>
              <Column width="30%" align="right">
                <Text style={style.metricsLabel}>{tt.display_total_sales}</Text>
              </Column>
            </Row>
          ))}
          <Hr />
          <Row>
            <Column width={"45%"}>
              <Text style={style.metricsLabel}>Total</Text>
            </Column>
            <Column width="25%" align="right">
              <Text
                style={style.metricsLabel}
              >{`${display_total_count} / ${display_total_ticket_quantity}`}</Text>
            </Column>
            <Column width="30%" align="right">
              <Text style={style.metricsLabel}>{display_total_sales}</Text>
            </Column>
          </Row>
        </Section>
      </RoundedContainer>
      <Footer cdn_url={cdn_url} />
    </Layout>
  );
}

const bodyText = {
  fontSize: "18px",
  lineHeight: "22px",
};

const style: Record<string, React.CSSProperties> = {
  metricsLabel: {
    fontSize: "16px",
    color: "black",
  },
  metricsValue: {
    fontSize: "18px",
    color: "black",
    fontWeight: "bold",
  },
  cardTitle: {
    color: "white",
    margin: 0,
    fontSize: "14px",
    textTransform: "uppercase",
    fontWeight: "bold",
  },
  rowLabel: {
    verticalAlign: "top",
    color: "#666",
  },
  rowValue: {
    fontWeight: "500",
  },
  eventDescription: {
    fontSize: "12px",
    color: "white",
    lineHeight: "18px",
    margin: 0,
  },
};

const helpText = {
  fontSize: "12px",
  margin: 0,
  lineHeight: "18px",
  marginBottom: "8px",
};

const centeredHelpText: React.CSSProperties = {
  ...helpText,
  textAlign: "center",
};

const title = {
  fontSize: "30px",
  fontWeight: 500,
  color: "black",
};

const containerTitle = {
  fontSize: "20px",
  fontWeight: 500,
  color: "black",
};

const label = {
  ...title,
  fontSize: "18px",
  color: "black",
  fontWeight: "normal",
};

const subtitle = {
  ...title,
  color: "#808080",
  fontSize: "26px",
};

template.PreviewProps = {
  event_name: "Test event",
  event_url: "/static/coachella.jpg",
  display_total_sales: "$1,245.23",
  display_period_sales: "$146.23",
  period: "07/10/2024 - 07/17/2024",
  report_date: new Date().toISOString(),
  cdn_url: "",
  organization_logo: "",
  period_label: "",
  event_description: `The best event of the year
  
Bring your friends

Long description, and so on and so forth

See you there!!!
`,
  faq_link: "kyd-faq.com",
  dashboard_url:
    "https://coachella.kydlabsdemo.com/manager/events/EV2f1de93f-17d7-4881-a2ff-c083c580dbdc",
};

export default template;
